import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import styles from './Button.module.css';

// 5 variants per spec/00 §0.6.9.
export type ButtonVariant =
  | 'filled-accent'
  | 'filled-danger'
  | 'ghost'
  | 'outline'
  | 'outline-accent';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  // Lucide icon (or any 16-px SVG). Renders left of label, or as the only
  // content when `iconOnly` is true. Per spec §0.10 use stroke-width 1.5 at
  // the icon definition site — Button does not enforce it (the icon is the
  // caller's responsibility).
  icon?: ReactNode;
  iconOnly?: boolean;
  loading?: boolean;
  // Visible text label. Required unless iconOnly.
  children?: ReactNode;
  // When the button is disabled, an aria-label or tooltip MUST explain why
  // (spec/00 §0.11 rule #11). The component does not auto-generate this — the
  // caller passes `title` or a wrapping <Tooltip>.
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  'filled-accent': styles.filledAccent ?? '',
  'filled-danger': styles.filledDanger ?? '',
  'ghost': styles.ghost ?? '',
  'outline': styles.outline ?? '',
  'outline-accent': styles.outlineAccent ?? '',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.sm ?? '',
  md: styles.md ?? '',
  lg: styles.lg ?? '',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'filled-accent',
    size = 'md',
    icon,
    iconOnly = false,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    styles.button,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    iconOnly ? styles.iconOnly : '',
    loading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : icon ? (
        <span className={styles.icon} aria-hidden={!iconOnly}>
          {icon}
        </span>
      ) : null}
      {!iconOnly && children}
    </button>
  );
});
